/**
 * The checkout's quote plumbing, shared by the two carts that price an
 * order: the storefront cart (StorefrontCart.vue) and the admin "Add New
 * Order" cart (Order/Cart.vue).
 *
 * Card #540 (https://trello.com/c/c6X9UiP6). The admin cart priced a
 * company-address, live-rate store with no ship-to, no live rate and no tax,
 * because it carried its own copy of the legacy OrderWrite location model
 * instead of the storefront's. Jake (2026-10-26): the admin screen decides
 * ship-to like the storefront. Rather than mount the 4,000-line storefront
 * cart, the pieces both carts need live here:
 *
 *   - `shipToQuoteLocation`: the SERVER's resolved destination
 *     (`checkout_quote` -> `ship_to`) as the location a live rate is asked
 *     for. A company / department store ships there; nobody enters it.
 *   - `liveShippingRequest` / `liveShippingQuoteKey` / `isUsableShippingQuote`:
 *     the `/shipping/getSmartShippingCost` request, the memo key it is stored
 *     under (checkoutQuoteMemo.js) and which answers are worth storing.
 *   - `requestCheckoutQuote`: the read-only `checkout_quote` RPC.
 *
 * NOT A TRUST BOUNDARY. Checkout recomputes every figure server-side and
 * charges the stored live quote; nothing here lets a client set a price.
 */
import { shippingQuoteKey } from "./checkoutQuoteMemo";
import { cartStoreArgs } from "@/router/storefrontStoreId";

/**
 * The store-memo key for one live shipping quote: exactly the inputs the
 * `/shipping/getSmartShippingCost` request is built from (see
 * `checkoutQuoteMemo.js` for why street and city are not among them).
 */
export function liveShippingQuoteKey(campaign, location, totalWeight) {
  if (!campaign) return null;
  const info = campaign.shipping_cost_info || {};
  return shippingQuoteKey({
    storeId: (campaign._jv && campaign._jv.id) ?? campaign.id,
    location,
    totalWeight,
    carrier: info.shippingCarrier ?? null,
    shippingType: info.shippingType ?? null,
    internationalShippingType: Object.hasOwn(info, "internationalShippingType")
      ? info.internationalShippingType
      : null,
  });
}

/**
 * The JSON:API error code checkout answers a refused shipping quote with
 * (`QUOTE_REFUSED_CODE` in api/src/services/shipping_quote.py).
 */
export const SHIPPING_QUOTE_REFUSED = "SHIPPING_QUOTE_REFUSED";

const SHIPPING_QUOTE_REFUSED_FALLBACK =
  "Your shipping quote has expired or no longer matches your order. " +
  "Please review the shipping cost and try again.";

/**
 * The server's message when `err` is checkout refusing the posted shipping
 * quote (expired, already spent by a placed order, or for another cart), or
 * null for any other failure. Bug Intake #700: this 409 used to fall into
 * the "Something changed about this campaign" dialog, which named the wrong
 * problem; the cart re-prices shipping and says what happened instead.
 */
export function shippingQuoteRefusalMessage(err) {
  const response = err && err.response;
  if (!response || response.status !== 409) return null;
  const errors = response.data && response.data.errors;
  const first = Array.isArray(errors) ? errors[0] : null;
  if (!first || first.code !== SHIPPING_QUOTE_REFUSED) return null;
  return typeof first.detail === "string" && first.detail.trim()
    ? first.detail.trim()
    : SHIPPING_QUOTE_REFUSED_FALLBACK;
}

/** A quote answer worth storing: a number, not a failure or "no rate". */
export function isUsableShippingQuote(response) {
  return Boolean(
    response &&
      response.data !== undefined &&
      response.data !== null &&
      !response.data.error
  );
}

/**
 * The `/shipping/getSmartShippingCost` body for `campaign`'s cart. The
 * ship-FROM is resolved by the server from the store (client company ->
 * distributor -> decorator), so the request names the store and never an
 * origin. A store naming no carrier / service sends null: the server applies
 * its defaults. `totalWeight` is in grams.
 */
export function liveShippingRequest(
  campaign,
  { location, totalWeight, userId, source }
) {
  const info = (campaign && campaign.shipping_cost_info) || {};
  return {
    storeId: (campaign._jv && campaign._jv.id) ?? campaign.id,
    shipToLocation: location,
    totalWeight,
    weightUnit: "gram",
    carrier: info.shippingCarrier ?? null,
    shippingType: info.shippingType ?? null,
    internationalShippingType: Object.hasOwn(info, "internationalShippingType")
      ? info.internationalShippingType
      : null,
    user_id: userId,
    source,
  };
}

/**
 * The server-resolved destination (`checkout_quote.ship_to`) in the location
 * shape a rate request and a tax lookup take, or null when the server
 * resolved nothing usable (`complete` false) or sent no address. The
 * distributor fallback is served as an area only (no street), on purpose.
 * The rate route completes the street from the store's own distributor
 * (`_complete_store_destination`, card #532), so the street-less location
 * is rated; never add the street to the shopper-facing payload.
 */
export function shipToQuoteLocation(shipTo) {
  const address = shipTo && shipTo.complete ? shipTo.address : null;
  if (!address) return null;
  return {
    street_and_number: address.address1 || "",
    suite_unit_etc: address.address2 || "",
    city: address.city || "",
    region: address.state || "",
    zip_code: address.postal_code || "",
    country: address.country || "US",
    isCompanyLocation: true,
  };
}

/**
 * POST the read-only `checkout_quote` RPC for `storeId` and return its
 * result. `args` are the RPC's own arguments (`buyer_location`,
 * `shipping_cost`, `shipping_quote_id`, `coupon_code`,
 * `client_department_id`; a pick-up location is not among them, since Bug
 * Intake #548 prices it like any other order). Throws on a failed request;
 * `config` is passed to axios (e.g. to suppress the error toast).
 */
export async function requestCheckoutQuote(http, accountId, storeId, args, config) {
  const response = await http.post(
    `/accounts/${accountId}/checkout_quote`,
    {
      meta: {
        method: "checkout_quote",
        args: { ...cartStoreArgs(storeId), ...args },
      },
    },
    config
  );
  return response && response.data && response.data.meta
    ? response.data.meta.result
    : null;
}
